import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ChevronRight, Lock } from "lucide-react";
import { useTokens } from "./TokenContext";
import { SKINS, SkinPatternOverlay } from "./skins";
import { toast } from "sonner";
import { useT } from "../context/LanguageContext";

export default function MySkins() {
  const { t } = useT();
  const { owned_skins, active_skin, applySkin, loadingSkins } = useTokens();
  const [busyId, setBusyId] = useState(null);

  if (loadingSkins || !owned_skins?.length) {
    return (
      <section data-testid="v2-myskins-empty">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#A78BFA]" /> {t("v2.myskins.title")}
          </h3>
        </div>
        <Link
          to="/v2/store"
          className="block bg-[#12121C] border border-dashed border-[#1F1F2A] rounded-2xl p-5 text-center hover:bg-[#161620] transition-colors"
          data-testid="v2-myskins-cta"
        >
          <Lock className="w-5 h-5 text-[#71717A] mx-auto mb-1.5" />
          <div className="text-xs text-[#A1A1AA]">{t("v2.myskins.empty")}</div>
          <div className="text-[11px] text-[#FCD34D] font-bold mt-1.5 uppercase tracking-wider">{t("v2.myskins.cta")}</div>
        </Link>
      </section>
    );
  }

  const apply = async (skinId) => {
    setBusyId(skinId);
    const target = active_skin === skinId ? null : skinId;
    const res = await applySkin(target);
    setBusyId(null);
    const skinName = t(`v2.skin.${skinId}.name`);
    if (res?.ok) toast.success(target ? t("v2.store.applied", { name: skinName }) : t("v2.store.removed"));
    else toast.error(res?.error || t("v2.store.errApply"));
  };

  return (
    <section data-testid="v2-myskins">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#A78BFA]" /> {t("v2.myskins.title")}
          <span className="text-[10px] text-[#71717A] font-mono uppercase tracking-wider">{t("v2.myskins.owned", { n: owned_skins.length })}</span>
        </h3>
        <Link to="/v2/store" className="text-[11px] font-medium text-[#A5B4FC] hover:text-white flex items-center gap-0.5" data-testid="v2-myskins-store-link">
          {t("v2.myskins.store")} <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {owned_skins.map((skinId) => {
          const sk = SKINS[skinId];
          if (!sk) return null;
          const isActive = active_skin === skinId;
          const Icon = sk.icon;
          const skinLabel = t(`v2.skin.${skinId}.label`);
          return (
            <div
              key={skinId}
              data-testid={`v2-myskin-${skinId}`}
              className={`relative rounded-2xl overflow-hidden border transition-all ${isActive ? "ring-2 ring-[#FCD34D]" : ""}`}
              style={{
                background: sk.bg,
                borderColor: sk.border,
                boxShadow: isActive ? sk.shadow : "none",
              }}
            >
              <SkinPatternOverlay skinId={skinId} />
              <div className="relative aspect-[4/3] flex flex-col items-center justify-center p-3">
                <Icon className="w-9 h-9 mb-1" style={{ color: sk.accent }} strokeWidth={1.8} />
                <div className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: sk.text }}>{skinLabel}</div>
                {isActive && (
                  <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FCD34D] text-[#3B2503]" data-testid={`v2-myskin-active-${skinId}`}>
                    <Check className="w-2.5 h-2.5" /> {t("v2.store.active")}
                  </span>
                )}
              </div>
              <div className="relative bg-[#0A0A0F]/60 backdrop-blur p-2 border-t" style={{ borderColor: sk.border }}>
                <button
                  type="button"
                  onClick={() => apply(skinId)}
                  disabled={busyId === skinId}
                  data-testid={`v2-myskin-apply-${skinId}`}
                  className={`w-full text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                    isActive
                      ? "bg-[#FCD34D]/15 text-[#FCD34D] hover:bg-[#FCD34D]/25"
                      : "bg-[#FCD34D] text-[#3B2503] hover:bg-[#FDE68A]"
                  }`}
                >
                  {busyId === skinId ? "…" : (isActive ? t("v2.store.remove") : t("v2.store.apply"))}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
